import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { Reveal, spotlight } from "@/components/Reveal";
import IMG from "@/lib/images";

const VALUES = [
    ["Prep before paint", "The finish is only as good as what's under it — we repair, sand, caulk and prime before any topcoat."],
    ["Written & itemized", "Clear scope, products and schedule in writing on every job, from a bedroom to a full subcontract package."],
    ["Respect for the space", "Floors and furniture protected, sites tidied daily, and a final walkthrough before we call it done."],
];

export default function About() {
    return (
        <div data-testid="about-page" className="pt-28 lg:pt-36">
            <section className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:px-16">
                <Reveal>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">The story of B&amp;B Painting and Renovation</p>
                    <h1 className="font-display mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                        Built on Quality. <span className="text-gold">Driven by Trust.</span>
                    </h1>
                    <p className="mt-6 text-base leading-relaxed text-slate-300">
                        At B&amp;B Painting and Renovation, our story is built on hard work,
                        attention to detail, and a passion for transforming spaces. With over 10
                        years of experience, we have built our reputation on delivering quality
                        workmanship, reliable service, and professional results that our clients
                        can trust.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-slate-300">
                        We specialize in residential and commercial painting, from custom homes
                        and new construction projects to renovations and commercial properties.
                        Whether we're working with homeowners, custom home builders, contractors,
                        or property managers, our goal is always the same: to deliver exceptional
                        finishes while making every project run smoothly.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-slate-300">
                        We understand that every project represents an investment. That's why we
                        focus on proper preparation, consistent quality, clean workspaces, and
                        completing projects on schedule. We believe that great painting is more
                        than just applying colour—it's about protecting your investment, enhancing
                        your space, and bringing your vision to life.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-slate-300">
                        Over the years, we've learned that strong relationships are just as
                        important as beautiful results. We take pride in being a dependable
                        partner for builders and contractors, helping keep projects on schedule
                        while delivering the quality their clients expect.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-slate-300">
                        As we continue to grow, our commitment remains unchanged: honest work,
                        dependable service, and quality you can see in every finish.
                    </p>
                    <div data-testid="about-qualifications-placeholder" className="placeholder-block mt-8 p-6">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Qualifications — placeholder</p>
                        <p className="mt-3 text-sm leading-relaxed text-slate-400">
                            [Add your licences, certifications, WSIB clearance, liability insurance
                            and workforce details once confirmed. They are not claimed anywhere on
                            this site until you provide them.]
                        </p>
                    </div>
                    <a href="https://share.google/ne3cGjXAE31IWlowE" target="_blank" rel="noopener noreferrer"
                        data-testid="about-google-link"
                        className="mt-8 inline-flex items-center gap-2 border border-gold/40 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-ink">
                        <Star size={14} fill="currentColor" strokeWidth={0} /> 5.0 on Google · 10 reviews
                    </a>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="relative">
                        <div className="clip-frame overflow-hidden">
                            <img src={IMG.painterLadder} alt="Painter at work — B&B Painting and Renovation" className="h-[440px] w-full object-cover lg:h-[560px]" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                        </div>
                        <div className="clip-frame-alt absolute -bottom-8 -left-4 hidden w-56 border-4 border-ink sm:block">
                            <img src={IMG.googleFoyer} alt="Completed interior by B&B Painting" className="h-40 w-full object-cover" />
                        </div>
                    </div>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
                <Reveal>
                    <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">How we work</h2>
                </Reveal>
                <div className="mt-12 grid gap-6 lg:grid-cols-3">
                    {VALUES.map(([t, d], i) => (
                        <Reveal key={t} delay={i * 0.08}>
                            <div onMouseMove={spotlight} data-testid={`about-value-${i}`} className="spot-card h-full border border-gold/15 bg-ink-3 p-8">
                                <span className="font-mono text-sm text-gold">0{i + 1}</span>
                                <h3 className="font-display mt-5 text-xl font-bold">{t}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">{d}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-y border-gold/15 bg-ink-2" data-testid="about-pullquote">
                <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-8">
                    <Reveal>
                        <p className="font-display text-2xl font-bold leading-snug text-slate-100 sm:text-3xl">
                            "We're not just painting walls. We're helping build spaces people are
                            proud to call home."
                        </p>
                        <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-gold">
                            Your vision. Our craftsmanship. A finish that makes the difference.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="border-y border-gold/15 bg-ink-2">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16">
                    <Reveal>
                        <h2 className="font-display text-2xl font-bold sm:text-3xl">Where we work</h2>
                        <p className="mt-3 max-w-xl text-sm text-slate-400">
                            Based in Ontario and serving the Greater Toronto Area and surrounding
                            communities with same-week estimates.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15} className="mt-12">
                        <Link to="/contact" data-testid="about-cta" className="group inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-ink transition-transform hover:-translate-y-0.5">
                            Get your free estimate <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}